import React, { useState, useEffect } from 'react';
import './Fichearticle.css';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Title, Tooltip, Legend,
} from 'chart.js';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  Title, Tooltip, Legend, annotationPlugin, DataLabelsPlugin,
);

/* ===================================================
   ★ CONFIGURATION
   =================================================== */
const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

const TYPES_AUTORISES = ['commande frn', 'commande ferme', 'prévision'];

// ★ Hauteur du graphique (px)
const HAUTEUR_GRAPHIQUE = 420;

// ★ Couleurs du graphique (charte MySSA)
const COULEURS = {
  stockProjete:  '#04202d',
  stockSimule:   '#0284c7',
  stockSecurite: '#be123c',
  prevision:     { fond: 'rgba(125, 211, 252, 0.75)', bord: '#0284c7' },
  commandeFerme: { fond: 'rgba(251, 113, 133, 0.75)', bord: '#be123c' },
  livraison:     { fond: 'rgba(253, 186, 116, 0.85)', bord: '#ea580c' },
};

const GRANULARITES = [
  { key: 'jour', label: 'Jour' },
  { key: 'semaine', label: 'Semaine' },
  { key: 'mois', label: 'Mois' },
];

/* ===================================================
   HELPERS GRANULARITÉ
   =================================================== */
const getWeekKey = (d) => {
  const tmp = new Date(d);
  tmp.setHours(0, 0, 0, 0);
  const day = tmp.getDay() || 7;
  tmp.setDate(tmp.getDate() - day + 1);
  const startOfYear = new Date(tmp.getFullYear(), 0, 1);
  const weekNum = Math.ceil(((tmp - startOfYear) / 86400000 + 1) / 7);
  return `${tmp.getFullYear()}-S${String(weekNum).padStart(2, '00')}`;
};

const getMonthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const aggregateRows = (rows, granularity) => {
  if (granularity === 'jour') return rows;
  const groups = {};
  rows.forEach((r) => {
    const d = new Date(r['date']);
    if (isNaN(d)) return;
    const key = granularity === 'semaine' ? getWeekKey(d) : getMonthKey(d);
    if (!groups[key]) {
      groups[key] = { lastRow: null, sumDepart: 0, sumLivraison: 0, sumPrevision: 0, sumCommandeFerme: 0 };
    }
    const g = groups[key];
    if (!g.lastRow || new Date(r['date']) > new Date(g.lastRow['date'])) g.lastRow = r;
    const lf = parseFloat(r['livraison fournisseur']);
    if (!isNaN(lf)) g.sumLivraison += lf;
    const dc = parseFloat(r['depart client']);
    if (!isNaN(dc)) {
      g.sumDepart += dc;
      if (r['type de donnee'] === 'prévision') g.sumPrevision += dc;
      if (r['type de donnee'] === 'commande ferme') g.sumCommandeFerme += dc;
    }
  });
  return Object.values(groups)
    .filter(g => g.lastRow)
    .map(g => ({
      ...g.lastRow,
      'depart client': g.sumDepart,
      'livraison fournisseur': g.sumLivraison,
      _sumPrevision: g.sumPrevision,
      _sumCommandeFerme: g.sumCommandeFerme,
    }))
    .sort((a, b) => new Date(a['date']) - new Date(b['date']));
};

const formatLabelGran = (dateStr, granularity) => {
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr || '';
  if (granularity === 'jour')
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  if (granularity === 'semaine') {
    const startOfYear = new Date(d.getFullYear(), 0, 1);
    const weekNum = Math.ceil(((d - startOfYear) / 86400000 + 1) / 7);
    return `S${String(weekNum).padStart(2, '0')} ${d.getFullYear()}`;
  }
  return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

const formatDateShort = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

const formatDateFull = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fr = (n) => (typeof n === 'number' && !isNaN(n) ? n.toLocaleString('fr-FR') : n);
const arrondi = (v) => (isNaN(Number(v)) || v === '' ? v : Math.round(Number(v)));

/* ===================================================
   COMPOSANT
   =================================================== */
const Fichearticle = ({ itemPrefill }) => {
  const [inputValue, setInputValue] = useState('');
  const [description, setDescription] = useState('');
  const [delaisApprovisionnement, setDelaisApprovisionnement] = useState('');
  const [delaisApprovisionnementReel, setDelaisApprovisionnementReel] = useState('');
  const [stockMini, setStockMini] = useState('');
  const [stockMain, setStockMain] = useState(0);
  const [dateLivraison, setDateLivraison] = useState('');
  const [totalLivraisonFournisseur, setTotalLivraisonFournisseur] = useState(0);
  const [totalCommandeClient, setTotalCommandeClient] = useState(0);
  const [totalPrevision, setTotalPrevision] = useState(0);
  const [totalBesoins, setTotalBesoins] = useState(0);
  const [data, setData] = useState([]);
  const [ruptureSeche, setRuptureSeche] = useState('');
  const [graphData, setGraphData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [overridesLivraison, setOverridesLivraison] = useState({});
  const [overridesPrevision, setOverridesPrevision] = useState({});
  const [granularity, setGranularity] = useState('jour');
  const [articleCourant, setArticleCourant] = useState('');
  const [introuvable, setIntrouvable] = useState(false);

  // Autocomplete
  const [allItems, setAllItems] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (!itemPrefill) document.title = 'MYSSA – Fiche article';
  }, [itemPrefill]);

  /* ---------- Chargement ---------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API}/donnee_histogramme`);
        if (!response.ok) throw new Error(`Erreur serveur : ${response.status}`);
        const jsonData = await response.json();
        setData(jsonData);
        const items = [...new Set(jsonData.map(d => d['Item']).filter(Boolean))].sort();
        setAllItems(items);
      } catch (error) {
        console.error('Erreur lors du chargement des données JSON:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (itemPrefill) setInputValue(itemPrefill);
  }, [itemPrefill]);

  useEffect(() => {
    if (itemPrefill && data.length > 0 && inputValue === itemPrefill) {
      lancerRecherche(itemPrefill, data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, inputValue, itemPrefill]);

  useEffect(() => {
    if (filteredData.length > 0) {
      const stockMiniValue = parseFloat(filteredData[0]['stock mini'] || 0);
      setGraphData(buildChartData(filteredData, stockMiniValue, overridesLivraison, overridesPrevision, granularity));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularity]);

  /* ---------- Données du graphique ---------- */
  const buildChartData = (filtered, stockMiniValue, currentOverridesLivraison, currentOverridesPrevision, gran = 'jour') => {
    const tableauFiltre = filtered.filter(item => TYPES_AUTORISES.includes(item['type de donnee']));
    const stockDepart = parseFloat(filtered[0]?.stock || 0);
    let stock = stockDepart;
    const stockSimuleMap = new Map();
    tableauFiltre.forEach((item, idx) => {
      const livraisonOrigine = parseFloat(item['livraison fournisseur']) || 0;
      const livraisonSimulee = currentOverridesLivraison[idx] !== undefined
        ? Number(currentOverridesLivraison[idx]) : livraisonOrigine;
      let besoin;
      if (item['type de donnee'] === 'prévision') {
        besoin = currentOverridesPrevision[idx] !== undefined
          ? Number(currentOverridesPrevision[idx])
          : (parseFloat(item['depart client']) || 0);
      } else {
        besoin = parseFloat(item['depart client']) || 0;
      }
      stock = stock + livraisonSimulee - besoin;
      stockSimuleMap.set(item['date'], Math.round(stock));
    });

    const hasOv =
      Object.keys(currentOverridesLivraison).length > 0 ||
      Object.keys(currentOverridesPrevision).length > 0;

    const aggFiltered = aggregateRows(filtered, gran);
    const aggTableauFiltre = aggregateRows(tableauFiltre, gran);

    const etiquettesBarres = {
      display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
      color: '#334155',
      anchor: 'end',
      align: 'top',
      font: { family: "'IBM Plex Mono', monospace", size: 10, weight: '600' },
    };

    return {
      labels: aggFiltered.map(item => formatLabelGran(item['date'], gran)),
      datasets: [
        {
          label: 'Stock projeté',
          data: aggFiltered.map(item => Math.round(parseFloat(item['stock projete']) || 0)),
          borderColor: COULEURS.stockProjete,
          backgroundColor: COULEURS.stockProjete,
          borderWidth: 2.5, pointRadius: 2.5, pointHoverRadius: 5, tension: 0.25,
          fill: false, type: 'line', order: 0,
          datalabels: { display: false },
        },
        ...(hasOv ? [{
          label: 'Stock projeté simulé',
          data: aggFiltered.map(item => stockSimuleMap.get(item['date']) ?? null),
          borderColor: COULEURS.stockSimule,
          backgroundColor: COULEURS.stockSimule,
          borderWidth: 2.5, borderDash: [6, 4], pointRadius: 2, tension: 0.25,
          fill: false, type: 'line', spanGaps: true, order: 0,
          datalabels: { display: false },
        }] : []),
        {
          label: 'Stock de sécurité',
          data: new Array(aggFiltered.length).fill(Math.round(stockMiniValue)),
          borderColor: COULEURS.stockSecurite,
          backgroundColor: COULEURS.stockSecurite,
          borderWidth: 1.5, borderDash: [4, 4], pointRadius: 0,
          fill: false, type: 'line', order: 1,
          datalabels: { display: false },
        },
        {
          label: 'Prévision',
          data: aggTableauFiltre.map(item =>
            gran === 'jour'
              ? (item['type de donnee'] === 'prévision' ? Math.round(parseFloat(item['depart client']) || 0) : 0)
              : Math.round(item._sumPrevision || 0)
          ),
          backgroundColor: COULEURS.prevision.fond, borderColor: COULEURS.prevision.bord,
          borderWidth: 1, borderRadius: 4, type: 'bar', stack: 'prevision', order: 2,
          datalabels: etiquettesBarres,
        },
        {
          label: 'Commande ferme',
          data: aggTableauFiltre.map(item =>
            gran === 'jour'
              ? (item['type de donnee'] === 'commande ferme' ? Math.round(parseFloat(item['depart client']) || 0) : 0)
              : Math.round(item._sumCommandeFerme || 0)
          ),
          backgroundColor: COULEURS.commandeFerme.fond, borderColor: COULEURS.commandeFerme.bord,
          borderWidth: 1, borderRadius: 4, type: 'bar', stack: 'commande', order: 2,
          datalabels: etiquettesBarres,
        },
        {
          label: 'Livraison fournisseur',
          data: aggTableauFiltre.map(item => Math.round(parseFloat(item['livraison fournisseur']) || 0)),
          backgroundColor: COULEURS.livraison.fond, borderColor: COULEURS.livraison.bord,
          borderWidth: 1, borderRadius: 4, type: 'bar', stack: 'livraison', order: 2,
          datalabels: etiquettesBarres,
        },
      ],
    };
  };

  /* ---------- Simulation ---------- */
  const handleOverrideLivraison = (idx, value) => {
    const next = { ...overridesLivraison, [idx]: value };
    setOverridesLivraison(next);
    if (filteredData.length > 0) {
      const stockMiniValue = parseFloat(filteredData[0]['stock mini'] || 0);
      setGraphData(buildChartData(filteredData, stockMiniValue, next, overridesPrevision, granularity));
    }
  };

  const handleOverridePrevision = (idx, value) => {
    const next = { ...overridesPrevision, [idx]: value };
    setOverridesPrevision(next);
    if (filteredData.length > 0) {
      const stockMiniValue = parseFloat(filteredData[0]['stock mini'] || 0);
      setGraphData(buildChartData(filteredData, stockMiniValue, overridesLivraison, next, granularity));
    }
  };

  const resetSimulation = () => {
    setOverridesLivraison({});
    setOverridesPrevision({});
    if (filteredData.length > 0) {
      const stockMiniValue = parseFloat(filteredData[0]['stock mini'] || 0);
      setGraphData(buildChartData(filteredData, stockMiniValue, {}, {}, granularity));
    }
  };

  /* ---------- Recherche ---------- */
  const lancerRecherche = (valeur, sourceData) => {
    setOverridesLivraison({});
    setOverridesPrevision({});
    const filtered = sourceData
      .filter(item => (item['Item'] || '').trim().toLowerCase() === valeur.trim().toLowerCase())
      .sort((a, b) => new Date(a['date']) - new Date(b['date']));
    setFilteredData(filtered);
    setArticleCourant(valeur.trim());
    if (filtered.length > 0) {
      setIntrouvable(false);
      setDescription(filtered[0].description);
      setDelaisApprovisionnement(filtered[0]['delais appro system'] || 'Délai non trouvé');
      setDelaisApprovisionnementReel(filtered[0]['delais appro reel'] ?? 'Délai non trouvé');
      setStockMini(filtered[0]['stock mini'] || '0');
      setStockMain(parseFloat(filtered[0].stock || 0));
      setDateLivraison(filtered[0]['prochaine date de livraison'] || 'Date non trouvée');
      setTotalLivraisonFournisseur(calculateTotalLivraisonFournisseur(filtered));
      setTotalCommandeClient(calculateTotalCommandeClient(filtered));
      setTotalPrevision(calculateTotalPrevision(filtered));
      setTotalBesoins(calculateTotalBesoins(filtered));
      checkRuptureSeche(filtered, valeur);
      const stockMiniValue = parseFloat(filtered[0]['stock mini'] || 0);
      setGraphData(buildChartData(filtered, stockMiniValue, {}, {}, granularity));
    } else {
      setIntrouvable(true);
      setDescription('Aucune correspondance trouvée');
      setDelaisApprovisionnement('Délai non trouvé');
      setDelaisApprovisionnementReel('Délai non trouvé');
      setStockMini('Stock non trouvé');
      setStockMain('Stock non trouvé');
      setDateLivraison('Date non trouvée');
      setTotalLivraisonFournisseur(0);
      setTotalCommandeClient(0);
      setTotalPrevision(0);
      setTotalBesoins(0);
      setRuptureSeche('');
      setGraphData(null);
    }
  };

  const checkRuptureSeche = (fd, valeur) => {
    for (const item of fd) {
      if (
        item['Item'] &&
        item['Item'].trim().toLowerCase() === valeur.trim().toLowerCase() &&
        item['rupture seche'] &&
        item['rupture seche'].toLowerCase().includes('rupture')
      ) { setRuptureSeche('oui'); return; }
    }
    setRuptureSeche('non');
  };

  const calculateTotalLivraisonFournisseur = (fd) =>
    Math.floor(fd.reduce((t, i) => t + (parseFloat(i['livraison fournisseur']) || 0), 0));
  const calculateTotalCommandeClient = (fd) =>
    Math.floor(fd.reduce((t, i) => (i['type de donnee'] === 'commande ferme' ? t + (parseFloat(i['depart client']) || 0) : t), 0));
  const calculateTotalPrevision = (fd) =>
    Math.floor(fd.reduce((t, i) => (i['type de donnee'] === 'prévision' ? t + (parseFloat(i['depart client']) || 0) : t), 0));
  const calculateTotalBesoins = (fd) =>
    Math.floor(fd.reduce((t, i) => t + (parseFloat(i['depart client']) || 0), 0));

  const handleSubmit = (event) => {
    event.preventDefault();
    setShowSuggestions(false);
    if (inputValue && data.length > 0) lancerRecherche(inputValue, data);
  };

  const suggestionsFiltrees = allItems
    .filter(r => r.toLowerCase().includes(inputValue.toLowerCase()))
    .slice(0, 30);

  /* ---------- Valeurs dérivées ---------- */
  const stockProjeteValue = typeof stockMain === 'number'
    ? stockMain + totalLivraisonFournisseur - totalBesoins
    : null;

  const tableauData = filteredData.filter(item => TYPES_AUTORISES.includes(item['type de donnee']));

  const hasOverride =
    Object.keys(overridesLivraison).length > 0 ||
    Object.keys(overridesPrevision).length > 0;

  const stockSimuleTableau = (() => {
    if (!filteredData.length) return [];
    let stock = parseFloat(filteredData[0]?.stock || 0);
    return tableauData.map((item, idx) => {
      const livraisonOrigine = parseFloat(item['livraison fournisseur']) || 0;
      const livraisonSimulee = overridesLivraison[idx] !== undefined ? Number(overridesLivraison[idx]) : livraisonOrigine;
      let besoin;
      if (item['type de donnee'] === 'prévision') {
        besoin = overridesPrevision[idx] !== undefined ? Number(overridesPrevision[idx]) : (parseFloat(item['depart client']) || 0);
      } else {
        besoin = parseFloat(item['depart client']) || 0;
      }
      stock = stock + livraisonSimulee - besoin;
      return Math.round(stock);
    });
  })();

  const stockSimuleFinal = stockSimuleTableau.length ? stockSimuleTableau[stockSimuleTableau.length - 1] : null;
  const sousStockMini = typeof stockMain === 'number' && Number(stockMini) > 0 && stockMain < Number(stockMini);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 18 } },
    scales: {
      x: {
        stacked: false,
        grid: { display: false },
        ticks: { maxRotation: 45, minRotation: 0, color: '#64748b', font: { family: "'IBM Plex Sans', sans-serif", size: 11 } },
      },
      y: {
        stacked: false,
        grid: { color: '#f1f5f9' },
        border: { display: false },
        ticks: { color: '#64748b', font: { family: "'IBM Plex Mono', monospace", size: 11 } },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'rectRounded',
          boxWidth: 10,
          boxHeight: 10,
          padding: 16,
          color: '#334155',
          font: { family: "'IBM Plex Sans', sans-serif", size: 12 },
        },
      },
      tooltip: {
        backgroundColor: '#04202d',
        titleFont: { family: "'IBM Plex Mono', monospace", size: 12 },
        bodyFont: { family: "'IBM Plex Sans', sans-serif", size: 12 },
        padding: 10,
        cornerRadius: 8,
      },
      datalabels: { color: '#334155' },
    },
    barPercentage: 0.8,
    categoryPercentage: 0.9,
  };

  const chargee = filteredData.length > 0;

  /* ===================================================
     RENDU
     =================================================== */
  return (
    <div className={`fa-page ${itemPrefill ? 'fa-embedded' : ''}`}>
      <div className="fa-container">

        {/* ── Header + recherche ── */}
        <div className="fa-header">
          <div className="fa-title-row">
            <div className="fa-accent" />
            <h1 className="fa-title">Fiche article</h1>
          </div>

          <form className="fa-search-form" onSubmit={handleSubmit}>
            <div className="fa-search">
              <svg className="fa-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
              </svg>
              <label htmlFor="fa-article" className="fa-sr-only">Référence article</label>
              <input
                type="text"
                id="fa-article"
                className="fa-search-input"
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder="Rechercher un article…"
                autoComplete="off"
              />
              {showSuggestions && inputValue && suggestionsFiltrees.length > 0 && (
                <div className="fa-suggestions">
                  {suggestionsFiltrees.map(ref => (
                    <button
                      type="button"
                      key={ref}
                      className="fa-suggestion"
                      onMouseDown={() => {
                        setInputValue(ref);
                        setShowSuggestions(false);
                        lancerRecherche(ref, data);
                      }}
                    >
                      {ref}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" className="fa-btn fa-btn-primary">Rechercher</button>
          </form>
        </div>

        {/* ── État vide / introuvable ── */}
        {!chargee && (
          <div className="fa-card fa-empty">
            {introuvable
              ? <>Aucune donnée trouvée pour <strong>{articleCourant}</strong>. Vérifie la référence.</>
              : 'Recherche un article pour afficher sa fiche, son stock projeté et simuler l’impact des commandes.'}
          </div>
        )}

        {chargee && (
          <>
            {/* ── Identité article ── */}
            <div className="fa-identity">
              <div className="fa-identity-main">
                <span className="fa-ref">{articleCourant}</span>
                <span className="fa-desc">{description}</span>
              </div>
              <div className="fa-badges">
                {ruptureSeche === 'oui' && <span className="fa-badge fa-badge-danger">Rupture sèche</span>}
                {ruptureSeche === 'non' && <span className="fa-badge fa-badge-ok">Pas de rupture sèche</span>}
                {sousStockMini && <span className="fa-badge fa-badge-warn">Sous le stock mini</span>}
                {hasOverride && <span className="fa-badge fa-badge-info">Simulation en cours</span>}
              </div>
            </div>

            {/* ── KPI ── */}
            <div className="fa-kpis">
              <div className="fa-kpi fa-kpi-dark">
                <div className="fa-kpi-label">Stock en main</div>
                <div className="fa-kpi-value">{fr(stockMain)}</div>
                <div className="fa-kpi-sub">stock mini {fr(arrondi(stockMini))}</div>
              </div>

              <div className={`fa-kpi ${stockProjeteValue !== null && stockProjeteValue < 0 ? 'fa-kpi-danger' : 'fa-kpi-ok'}`}>
                <div className="fa-kpi-label">Stock projeté fin d’horizon</div>
                <div className="fa-kpi-value">{stockProjeteValue !== null ? fr(stockProjeteValue) : '—'}</div>
                <div className="fa-kpi-sub">
                  {hasOverride && stockSimuleFinal !== null
                    ? <>simulé : <strong>{fr(stockSimuleFinal)}</strong></>
                    : 'stock + cmdes frn − besoins'}
                </div>
              </div>

              <div className="fa-kpi">
                <div className="fa-kpi-label">Délai d’approvisionnement</div>
                <div className="fa-kpi-value">{fr(arrondi(delaisApprovisionnement))}<span className="fa-kpi-unit"> j</span></div>
                <div className="fa-kpi-sub">réel constaté : <strong>{fr(arrondi(delaisApprovisionnementReel))} j</strong></div>
              </div>

              <div className="fa-kpi">
                <div className="fa-kpi-label">Prochaine livraison possible</div>
                <div className="fa-kpi-value fa-kpi-value-sm">{formatDateFull(dateLivraison)}</div>
                <div className="fa-kpi-sub">si commande passée aujourd’hui</div>
              </div>
            </div>

            {/* ── Flux sur l'horizon ── */}
            <div className="fa-card fa-flux">
              <div className="fa-flux-item">
                <span className="fa-dot fa-dot-livraison" />
                <div><div className="fa-flux-label">Commandes fournisseurs</div><div className="fa-flux-value">{fr(totalLivraisonFournisseur)}</div></div>
              </div>
              <div className="fa-flux-item">
                <span className="fa-dot fa-dot-ferme" />
                <div><div className="fa-flux-label">Commandes clients</div><div className="fa-flux-value">{fr(totalCommandeClient)}</div></div>
              </div>
              <div className="fa-flux-item">
                <span className="fa-dot fa-dot-prevision" />
                <div><div className="fa-flux-label">Prévisions</div><div className="fa-flux-value">{fr(totalPrevision)}</div></div>
              </div>
              <div className="fa-flux-item fa-flux-total">
                <div><div className="fa-flux-label">Total besoins</div><div className="fa-flux-value">{fr(totalBesoins)}</div></div>
              </div>
            </div>

            {/* ── Graphique ── */}
            {graphData && (
              <div className="fa-card fa-chart-card">
                <div className="fa-card-head">
                  <div>
                    <div className="fa-card-title">Stock projeté et flux</div>
                    <div className="fa-card-sub">Barres : prévisions, commandes clients et livraisons fournisseurs · lignes : stock</div>
                  </div>
                  <div className="fa-seg" role="group" aria-label="Granularité du graphique">
                    {GRANULARITES.map(g => (
                      <button
                        key={g.key}
                        type="button"
                        aria-pressed={granularity === g.key}
                        className={`fa-seg-btn ${granularity === g.key ? 'active' : ''}`}
                        onClick={() => setGranularity(g.key)}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="fa-chart-wrap" style={{ height: `${HAUTEUR_GRAPHIQUE}px` }}>
                  <Line data={graphData} options={chartOptions} />
                </div>
              </div>
            )}

            {/* ── Tableau de simulation ── */}
            {tableauData.length > 0 && (
              <div className="fa-card fa-table-card">
                <div className="fa-card-head">
                  <div>
                    <div className="fa-card-title">Simulation</div>
                    <div className="fa-card-sub">Modifie une commande fournisseur ou une prévision pour voir l’impact sur le stock</div>
                  </div>
                  {hasOverride && (
                    <button type="button" className="fa-btn fa-btn-ghost" onClick={resetSimulation}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" /><path d="M3 3v5h5" />
                      </svg>
                      Réinitialiser
                    </button>
                  )}
                </div>

                <div className="fa-table-scroll">
                  <table className="fa-table">
                    <thead>
                      <tr>
                        <th className="fa-th-label" />
                        {tableauData.map((item, index) => (
                          <th key={index}>{formatDateShort(item['date'])}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="fa-td-label"><span className="fa-dot fa-dot-livraison" />Cmde frn</td>
                        {tableauData.map((item, index) => {
                          const valOrigine = Math.round(parseFloat(item['livraison fournisseur']) || 0);
                          const valAffichee = overridesLivraison[index] !== undefined ? overridesLivraison[index] : valOrigine;
                          const estModifie = overridesLivraison[index] !== undefined && Number(overridesLivraison[index]) !== valOrigine;
                          return (
                            <td key={index} className="fa-td-input">
                              <input
                                type="number"
                                className={`fa-cell-input ${estModifie ? 'modified' : ''}`}
                                value={valAffichee}
                                onChange={(e) => handleOverrideLivraison(index, e.target.value)}
                                aria-label={`Commande fournisseur ${formatDateShort(item['date'])}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td className="fa-td-label"><span className="fa-dot fa-dot-prevision" />Prévision</td>
                        {tableauData.map((item, index) => {
                          const estPrevision = item['type de donnee'] === 'prévision';
                          const valOrigine = estPrevision ? Math.round(parseFloat(item['depart client']) || 0) : 0;
                          const valAffichee = overridesPrevision[index] !== undefined ? overridesPrevision[index] : valOrigine;
                          const estModifie = overridesPrevision[index] !== undefined && Number(overridesPrevision[index]) !== valOrigine;
                          return (
                            <td key={index} className="fa-td-input">
                              {estPrevision ? (
                                <input
                                  type="number"
                                  className={`fa-cell-input ${estModifie ? 'modified' : ''}`}
                                  value={valAffichee}
                                  onChange={(e) => handleOverridePrevision(index, e.target.value)}
                                  aria-label={`Prévision ${formatDateShort(item['date'])}`}
                                />
                              ) : (
                                <span className="fa-zero">0</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td className="fa-td-label"><span className="fa-dot fa-dot-ferme" />Cmde client</td>
                        {tableauData.map((item, index) => {
                          const v = item['type de donnee'] === 'commande ferme' ? Math.round(item['depart client']) : 0;
                          return <td key={index} className="fa-td-num">{v ? fr(v) : <span className="fa-zero">0</span>}</td>;
                        })}
                      </tr>
                      <tr className="fa-row-stock">
                        <td className="fa-td-label"><span className="fa-dot fa-dot-stock" />Stock projeté</td>
                        {tableauData.map((item, index) => {
                          const v = Math.round(item['stock projete']);
                          return (
                            <td key={index} className={`fa-td-num fa-stock ${v >= 0 ? 'pos' : 'neg'}`}>{fr(v)}</td>
                          );
                        })}
                      </tr>
                      {hasOverride && (
                        <tr className="fa-row-simule">
                          <td className="fa-td-label"><span className="fa-dot fa-dot-simule" />Stock simulé</td>
                          {stockSimuleTableau.map((val, index) => (
                            <td key={index} className={`fa-td-num fa-simule ${val >= 0 ? 'pos' : 'neg'}`}>{fr(val)}</td>
                          ))}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Fichearticle;